import { TreeViewPersonGroupModel } from '@/model/EnrollmentModel';
import { AddPersonGroupIcon } from '@/resources/Icons';
import { useStoreDispatch, useStoreSelector } from '@/store';
import { setSelectedPersonGroup } from '@/store/enrollment/actions';
import { selectPersonGroups, selectSelectedPersonGroup } from '@/store/enrollment/selectors';
import { Button, Tooltip, TreeSelect } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import styles from '../enrollmentTab.module.scss';
import { PersonGroupsModal } from './PersonGroupsModal/PersonGroupsModal';

const enrollmentPersonGroupsSelectId: string = 'enrollmentPersonGroupsSelect';
const addPersonGroupStyles: CSSProperties = { fontSize: 24 };

type Props = {
	onSelectPersonGroup: () => void;
};

const PersonGroupSelect: React.FC<Props> = ({ onSelectPersonGroup }) => {
	//#region State Management Variables
	const dispatch = useStoreDispatch();
	const [treeData, setTreeData] = useState<DefaultOptionType[]>([]);
	const [selectedNode, setSelectedNode] = useState<string>('');
	const [showPersonGroupModal, setShowPersonGroupModal] = useState<boolean>(false);
	const isFirstRun = useRef<boolean>(true);
	const personGroups: TreeViewPersonGroupModel[] = useStoreSelector<TreeViewPersonGroupModel[]>(selectPersonGroups);
	const selectedPersonGroups: TreeViewPersonGroupModel = useStoreSelector<TreeViewPersonGroupModel>(selectSelectedPersonGroup);
	//#endregion

	useEffect(() => {
		if (personGroups.length > 0) {
			setTreeData(getRenderTreeNodes(personGroups));

			if (!selectedPersonGroups) {
				const index: number = personGroups.length === 1 ? 0 : 1;
				const firstPersonGroupToLoad: TreeViewPersonGroupModel = {
					GroupId: personGroups[index].GroupId,
					FolderId: personGroups[index].FolderId,
					Name: personGroups[index].Name,
				};

				dispatch(setSelectedPersonGroup(firstPersonGroupToLoad));
				setSelectedNode(`${firstPersonGroupToLoad.GroupId},${firstPersonGroupToLoad.FolderId}`);
			} else if (selectedPersonGroups && isFirstRun.current) {
				if (selectedPersonGroups.GroupId === 0) {
					setSelectedSubGroupNode(personGroups, selectedPersonGroups);
				} else {
					setSelectedGroupNode(personGroups, selectedPersonGroups);
				}
			}
		}
	}, [personGroups]);

	const setSelectedGroupNode = (currentPersonGroups: TreeViewPersonGroupModel[], currentSelectedPersonGroup: TreeViewPersonGroupModel): void => {
		const groupIndex: number = currentPersonGroups.findIndex(x => x.GroupId === currentSelectedPersonGroup.GroupId);
		if (groupIndex === -1) {
			const firstGroupIndex: number = currentPersonGroups.length === 1 ? 0 : 1;
			setSelectedNode(`${currentPersonGroups[firstGroupIndex].GroupId},${currentPersonGroups[firstGroupIndex].FolderId}`);
		} else {
			setSelectedNode(`${currentSelectedPersonGroup.GroupId},${currentSelectedPersonGroup.FolderId}`);
		}
	};

	const setSelectedSubGroupNode = (currentPersonGroups: TreeViewPersonGroupModel[], currentSelectedPersonGroup: TreeViewPersonGroupModel): void => {
		currentPersonGroups.every((personGroup: TreeViewPersonGroupModel) => {
			let continueLoop: boolean = true;

			const previousSubGroup: TreeViewPersonGroupModel = personGroup.ChildGroups.find(
				(subGroup: TreeViewPersonGroupModel) => subGroup.GroupId === 0 && subGroup.FolderId === currentSelectedPersonGroup.FolderId
			);

			if (previousSubGroup) {
				setSelectedNode(`${previousSubGroup.GroupId},${previousSubGroup.FolderId}`);
				continueLoop = false;
			} else if (personGroup.ChildGroups.length > 0) {
				setSelectedSubGroupNode(personGroup.ChildGroups, currentSelectedPersonGroup);
			}

			return continueLoop;
		});
	};

	const handleOnSelectGroup = (value, node) => {
		if (value) {
			const groupValues: string[] = value.split(',');
			const selectedGroup: TreeViewPersonGroupModel = {
				GroupId: Number(groupValues[0]),
				FolderId: Number(groupValues[1]),
				Name: node.title,
			};

			dispatch(setSelectedPersonGroup(selectedGroup));
			onSelectPersonGroup();
			setSelectedNode(value);
		}
	};

	const getRenderTreeNodes = (personGroups: TreeViewPersonGroupModel[]): DefaultOptionType[] =>
		personGroups.map<DefaultOptionType>((group: TreeViewPersonGroupModel) => {
			const groupValue: string = `${group.GroupId},${group.FolderId}`;
			if (group.ChildGroups) {
				return { value: groupValue, children: getRenderTreeNodes(group.ChildGroups), title: group.Name };
			}

			return { value: groupValue, title: group.Name };
		});

	return (
		<div>
			<label htmlFor={enrollmentPersonGroupsSelectId}>{_('PersonGroups')}</label>
			<TreeSelect
				id={enrollmentPersonGroupsSelectId}
				className={styles.personGroupSelect}
				popupClassName={styles.personGroupDropdown}
				onSelect={handleOnSelectGroup}
				value={selectedNode}
				treeData={treeData}
			/>
			<Tooltip placement='top' title={_('PersonGroups')}>
				<Button
					className={styles.personGroupsModalButton}
					htmlType='button'
					icon={<AddPersonGroupIcon style={addPersonGroupStyles} />}
					onClick={() => setShowPersonGroupModal(true)}
				/>
			</Tooltip>
			{showPersonGroupModal && <PersonGroupsModal setShowModal={setShowPersonGroupModal} />}
		</div>
	);
};

export { PersonGroupSelect };
